import React, { useMemo } from "react";
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactSVG } from "react-svg";
import Progress from "react-svg-progress";
import moment from "moment";
import I18n from "i18n-js";

import LeaderActions from "../components/LeaderActions";

import sortAscIcon from "../../assets/icons/table-sort-asc.svg";
import sortDescIcon from "../../assets/icons/table-sort-desc.svg";

const formatDate = (date) => {
  const offsetDate = moment.utc(date).local().toDate();
  return I18n.l("date.formats.default", offsetDate);
}

const makeTableData = (user, plans) => plans.map(plan => {
  const endDate = plan?.status === "completed" ? plan.completed_at : plan.target_date;
  return {
    uuid: plan.uuid,
    title: plan.title,
    timeframe: `${formatDate(plan.start_date)} - ${formatDate(endDate)}`,
    status: plan.status,
    confirmedAt: plan.confirmed_at ? formatDate(plan.confirmed_at) : "",
    user_uuid: plan.user_uuid,
    canAccess: (user?.uuid === plan.user_uuid) || plan.status !== "draft"
  };
});

const PlansTable = ({ loading, user, plans, isTeamLead }) => {

  console.log(loading, plans)

  const columns = useMemo(() => [
    {
      id: "title",
      accessorKey: "title",
      header: () => <span>{I18n.t("pdp.all.table.columns.label")}</span>,
      cell: info => renderPlanTitle(info),
      sortUndefined: "last",
      sortDescFirst: false
    },
    {
      id: "timeframe",
      accessorKey: "timeframe",
      header: () => <span>{I18n.t("pdp.all.table.columns.timeframe")}</span>,
      cell: info => info.getValue(),
      sortUndefined: "last",
      sortDescFirst: false
    },
    {
      id: "status",
      accessorKey: "status",
      header: () => <span>{I18n.t("pdp.all.table.columns.status")}</span>,
      cell: info => (
        <span className={info.getValue()}>
          {I18n.t(`pdp.statuses.${info.getValue()}`)}
        </span>
      ),
      sortUndefined: "last",
      sortDescFirst: false
    },
    {
      id: "confirmedAt",
      accessorKey: "confirmedAt",
      header: () => <span>{I18n.t("pdp.all.table.columns.confirmed_at")}</span>,
      cell: info => info.getValue(),
      sortUndefined: "last",
      sortDescFirst: false
    },
    {
      id: "actions",
      header: () => <span>{I18n.t("pdp.all.table.columns.actions")}</span>,
      cell: info => <LeaderActions plan={info.row.original} />
    }
  ], []);

  const table = useReactTable({
    columns,
    data: makeTableData(user, plans),
    initialState: {
      columnVisibility: {
        confirmedAt: isTeamLead,
        actions: isTeamLead
      }
    },
    debugTable: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const renderPlanTitle = (info) => {
    if (info.row.original.canAccess) {
      return (
        <a href={`${info.row.original.uuid}?locale=${I18n.locale}`}>
          {info.getValue()}
        </a>
      );
    }

    return info.getValue();
  }

  return (
    <div className="table-wrapper">
      <table>
        <thead>
          {table.getHeaderGroups().map(group => (
            <tr key={group.id}>
              {group.headers.map(header => (
                <th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <div
                      className={header.column.getCanSort() ? "sorting-column" : ""}
                      title={header.column.getCanSort()
                        ? header.column.getNextSortingOrder() === "asc"
                          ? I18n.t("pdp.all.table.sort.asc")
                          : header.column.getNextSortingOrder() === "desc"
                            ? I18n.t("pdp.all.table.sort.desc")
                            : I18n.t("pdp.all.table.sort.clear")
                        : undefined
                      }
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() ? (
                        <div className="sorting">
                          <ReactSVG className={`icon-asc ${header.column.getIsSorted()}`} src={sortAscIcon} />
                          <ReactSVG className={`icon-desc ${header.column.getIsSorted()}`} src={sortDescIcon} />
                        </div>
                      ) : null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading
            ? (
                <div className="progress-wrapper">
                  <Progress color="#265F78" size="30px" />
                </div>
              )
            : table.getRowModel().rows.length === 0
              ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center", padding: "1rem" }}>
                    {I18n.t("pdp.all.table.no_items")}
                  </td>
                </tr>
              )
              : table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
          }
        </tbody>
      </table>
    </div>
  );
}

export default PlansTable;
